import type { ValidatedAssets } from "../core/AssetLoader";
import { createCameraConfig } from "../core/CameraManager";
import { createInitialLevelState } from "../model/LevelState";
import { createEmptyNoteState } from "../model/NoteState";
import type { CameraConfig } from "../types/camera";
import type { LevelData, LevelRuntimeConstraints } from "../types/level";
import type { LevelState } from "../model/LevelState";
import type { NoteState } from "../model/NoteState";

export interface LevelSceneInitResult {
  readonly levelData: LevelData;
  readonly levelState: LevelState;
  readonly noteState: NoteState;
  readonly cameraConfig: CameraConfig;
  readonly runtimeConstraints: LevelRuntimeConstraints | undefined;
}

export interface LevelSceneData {
  readonly levelId: string;
  readonly transitionIn?: LevelSceneTransitionInData;
}

export interface LevelSceneTransitionInData {
  readonly color: "white" | "black";
  readonly durationMs: number;
}

export const ESC_TARGET_SCENE = "LevelSelectScene";
export const RESTART_TARGET_SCENE = "LevelScene";

export function initializeLevelScene(
  levelId: string,
  assets: ValidatedAssets,
): LevelSceneInitResult {
  const levelData = assets.levels[levelId];
  if (levelData === undefined) {
    throw new Error(`Level ${levelId} not found in validated assets.`);
  }

  const dictionary = assets.symbols;
  const save = assets.save;

  const permanentReveal = save.fog.permanentRevealLevels.includes(levelId);
  const playerNotes = save.symbols.notes;

  const levelState = createInitialLevelState(levelData, dictionary, {
    playerNotes,
    permanentReveal,
    triggeredBreadcrumbIds: save.triggeredBreadcrumbs,
  });

  let noteState = createEmptyNoteState();
  for (const [symbol, guess] of Object.entries(playerNotes)) {
    if (guess !== null) {
      noteState = { guesses: { ...noteState.guesses, [symbol]: guess } };
    }
  }

  const cameraConfig = createCameraConfig(levelData.camera.initialCenter, levelData.camera.mode);

  return { levelData, levelState, noteState, cameraConfig, runtimeConstraints: levelData.runtimeConstraints };
}
